import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { API } from "@/lib/api";

const DEFAULT_BRAND = {
  active: true,
  source: "platform",
  product_name: "WA Calls",
  website_title: "WA Calls — AI Voice Agents",
  slogan: "AI Voice Agents",
  tagline: "AI Voice Agents",
  website_url: "https://wacalls.com",
  logo_url: null,
  login_logo_url: null,
  favicon_url: null,
  domain: null,
};

/** Empty brand shown on white-label hosts until API (or cache) resolves — never flash WA Calls. */
const PENDING_BRAND = {
  active: false,
  source: null,
  product_name: "",
  website_title: "",
  slogan: "",
  tagline: "",
  website_url: null,
  logo_url: null,
  login_logo_url: null,
  favicon_url: null,
  domain: null,
  pending: true,
};

const PLATFORM_HOSTS = new Set([
  "wacalls.com",
  "www.wacalls.com",
  "dev.wacalls.com",
  "www.dev.wacalls.com",
  "vicibots.com",
  "www.vicibots.com",
  "localhost",
  "127.0.0.1",
]);

const CACHE_PREFIX = "wl-brand:";

const BrandContext = createContext({
  brand: DEFAULT_BRAND,
  ready: false,
  reload: () => {},
});

export function isPlatformHost(host = typeof window !== "undefined" ? window.location.hostname : "") {
  const h = (host || "").toLowerCase();
  if (PLATFORM_HOSTS.has(h)) return true;
  if (h.endsWith(".wacalls.com") || h.endsWith(".vicibots.com")) return true;
  return false;
}

function cacheKey(host) {
  return `${CACHE_PREFIX}${host}`;
}

function normalizeBrand(data, host) {
  const slogan = data.slogan || data.tagline || DEFAULT_BRAND.slogan;
  return {
    active: data.active !== false,
    source: data.source || (data.domain ? "reseller" : "platform"),
    product_name: data.product_name || DEFAULT_BRAND.product_name,
    website_title: data.website_title || data.product_name || DEFAULT_BRAND.website_title,
    slogan,
    tagline: slogan,
    website_url: data.website_url || null,
    logo_url: data.logo_url || null,
    login_logo_url: data.login_logo_url || null,
    favicon_url: data.favicon_url || null,
    domain: data.domain || host || null,
    pending: false,
  };
}

function readCachedBrand(host) {
  try {
    const raw = localStorage.getItem(cacheKey(host));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.product_name) return null;
    // Reseller WL cache requires active; platform cache may be stored too.
    if (data.source === "reseller" && !data.active) return null;
    return normalizeBrand(data, host);
  } catch {
    return null;
  }
}

function writeCachedBrand(host, brand) {
  try {
    if (brand?.product_name && !brand.pending) {
      localStorage.setItem(cacheKey(host), JSON.stringify({
        active: brand.active !== false,
        source: brand.source,
        product_name: brand.product_name,
        website_title: brand.website_title,
        slogan: brand.slogan,
        tagline: brand.tagline,
        website_url: brand.website_url,
        logo_url: brand.logo_url,
        login_logo_url: brand.login_logo_url,
        favicon_url: brand.favicon_url,
        domain: brand.domain,
      }));
    } else {
      localStorage.removeItem(cacheKey(host));
    }
  } catch {
    /* private mode / quota */
  }
}

function applyDocumentBrand(brand, { allowDefaultTitle = true } = {}) {
  if (brand.website_title && !brand.pending) {
    document.title = brand.website_title;
  } else if (allowDefaultTitle && !brand.pending) {
    document.title = DEFAULT_BRAND.website_title;
  } else if (brand.pending) {
    document.title = brand.website_title || "\u200b";
  }

  if (!brand.favicon_url) return;
  let link = document.querySelector("link[rel='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = brand.favicon_url;
}

function initialBrand() {
  if (typeof window === "undefined") return DEFAULT_BRAND;
  const host = window.location.hostname;
  const injected = window.__WL_BRAND__;
  if (injected?.product_name) {
    const next = normalizeBrand(injected, host);
    applyDocumentBrand(next);
    return next;
  }
  const cached = readCachedBrand(host);
  if (cached) {
    applyDocumentBrand(cached);
    return cached;
  }
  if (isPlatformHost(host)) return DEFAULT_BRAND;
  applyDocumentBrand(PENDING_BRAND, { allowDefaultTitle: false });
  return PENDING_BRAND;
}

async function fetchBranding(host) {
  const qs = `host=${encodeURIComponent(host)}`;
  const urls = [
    `/api/white-label/public/branding?${qs}`,
    `${API}/white-label/public/branding?${qs}`,
  ];
  const seen = new Set();
  for (const url of urls) {
    if (seen.has(url)) continue;
    seen.add(url);
    try {
      const r = await fetch(url, { credentials: "omit" });
      if (!r.ok) continue;
      const data = await r.json();
      if (data?.product_name) return data;
    } catch {
      /* try next */
    }
  }
  return null;
}

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(initialBrand);
  const [ready, setReady] = useState(() => {
    if (typeof window === "undefined") return false;
    const host = window.location.hostname;
    return isPlatformHost(host) || !!readCachedBrand(host);
  });

  const reload = useCallback(async () => {
    const host = window.location.hostname;
    const data = await fetchBranding(host);
    if (data?.product_name) {
      const next = normalizeBrand(data, host);
      setBrand(next);
      writeCachedBrand(host, next);
      applyDocumentBrand(next);
    } else if (isPlatformHost(host)) {
      writeCachedBrand(host, DEFAULT_BRAND);
      setBrand(DEFAULT_BRAND);
      applyDocumentBrand(DEFAULT_BRAND);
    } else {
      writeCachedBrand(host, null);
      setBrand(PENDING_BRAND);
      applyDocumentBrand(PENDING_BRAND, { allowDefaultTitle: false });
    }
    setReady(true);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const value = useMemo(() => ({ brand, ready, reload }), [brand, ready, reload]);
  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  return useContext(BrandContext);
}

/** Name to render in chrome — empty while a white-label host is still resolving. */
export function useProductName() {
  const { brand, ready } = useBrand();
  if (brand.product_name) return brand.product_name;
  if (brand.pending || !ready) return "";
  return DEFAULT_BRAND.product_name;
}

export function useSlogan() {
  const { brand, ready } = useBrand();
  if (brand.slogan || brand.tagline) return brand.slogan || brand.tagline;
  if (brand.pending || !ready) return "";
  return DEFAULT_BRAND.slogan;
}

export { DEFAULT_BRAND, PENDING_BRAND };
